import classes from './surveys.module.css';
import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { isProfileComplete, buildCpxProfileParams, buildRapidoLocale, buildRapidoDOB, buildRapidoGender } from '../../utils/profile';
import { getUserGeo } from '../../services/userGeoService';
import { getRapidoReachSurveys, normalizeRapidoSurvey } from '../../services/rapidoreachService';
import CategorySelector from '../Offers/CategorySelector';
import TopOffer from '../../components/TopOffer/TopOffer';
import JackpotStrip from '../../components/JackpotStrip/JackpotStrip';
import useDarkOffersBackground from '../../hooks/useDarkOffersBackground';
import { getPlayingNow } from '../../utils/playingNow';

const SurveyList = () => {
    const { user } = useAuth();
    const [ip, setIP] = useState("");
    const [city, setCity] = useState("");
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useDarkOffersBackground();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    // CPX redirects here after a survey with ?cpx_message_id={message_id}.
    // The wall iframe is the only thing that can redeem it into an exact
    // outcome, so we show our own generic confirmation instead, then clean
    // the param so a refresh doesn't re-show it.
    const [showCompletion, setShowCompletion] = useState(
        () => params.has('cpx_message_id') || params.has('message_id')
    );

    useEffect(() => {
        if (params.has('cpx_message_id') || params.has('message_id')) {
            navigate(location.pathname, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const getUserIP = async () => {
            try {
                const res = await axios.get("https://api.ipify.org/?format=json");
                const fetchedIP = res.data.ip;
                setIP(fetchedIP);
                // RapidoReach requires City — ipgeolocation.io (via /api/usergeo) returns it.
                try {
                    const geo = await getUserGeo(fetchedIP);
                    if (geo?.city) setCity(geo.city);
                } catch (geoErr) {
                    console.warn('Failed to fetch geo/city:', geoErr?.message);
                }
            } catch (err) {
                setError(err.message || 'Failed to fetch IP');
                setLoading(false);
            }
        };
        getUserIP();
    }, []);

    useEffect(() => {
        if (!user?.id || !ip) return;

        let cancelled = false;

        const fetchSurveys = async () => {
            try {
                const cpxPromise = fetch(
                    `https://live-api.cpx-research.com/api/get-surveys.php?app_id=21139&ext_user_id=${user.id}&output_method=api&ip_user=${ip}&limit=30${buildCpxProfileParams(user)}`
                ).then(r => r.json());
                // City is required by RapidoReach — skip it (not CPX) when geo lookup failed.
                const rapidoPromise = city
                    ? getRapidoReachSurveys({
                        userId: user.id,
                        ipAddress: ip,
                        city,
                        countryLanguageCode: buildRapidoLocale(user),
                        dateOfBirth: buildRapidoDOB(user),
                        gender: buildRapidoGender(user),
                        zipCode: user?.zipCode,
                    })
                    : Promise.resolve(null);
                const [cpxResult, rapidoResult] = await Promise.allSettled([cpxPromise, rapidoPromise]);
                if (!cancelled) {
                    const cpxSurveys = cpxResult.status === 'fulfilled' && Array.isArray(cpxResult.value?.surveys)
                        ? cpxResult.value.surveys
                        : [];
                    if (cpxResult.status === 'rejected') console.warn('CPX fetch failed:', cpxResult.reason?.message);
                    const rapidoSurveys = rapidoResult.status === 'fulfilled' && Array.isArray(rapidoResult.value?.Data)
                        ? rapidoResult.value.Data.map(normalizeRapidoSurvey)
                        : [];
                    if (rapidoResult.status === 'rejected') console.warn('RapidoReach fetch failed:', rapidoResult.reason?.message);
                    setSurveys([...cpxSurveys, ...rapidoSurveys]);
                    setLoading(false);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err.message);
                    setLoading(false);
                }
            }
        };

        fetchSurveys();
        return () => {
            cancelled = true;
        };
    }, [user?.id, user?.birthDay, user?.birthMonth, user?.birthYear, user?.gender, user?.countryCode, user?.zipCode, ip, city]);

  // Onboarding gate: users without profiling info can't enter surveys.
  if (user && !isProfileComplete(user)) {
    return <Navigate to={`/profile-setup?returnUrl=${encodeURIComponent(location.pathname)}`} replace />;
  }

  if (loading) {
    return <div style={{ color: '#f3e8ff', padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  if (error) {
    return <div style={{ color: '#f3e8ff', padding: '2rem', textAlign: 'center' }}>Error: {error}</div>;
  }

  return (
    <>
        {/* SURVEYS-ONLY TEST: tabs hidden (Explore would just redirect here). */}
        {/* <CategorySelector /> */}
    <div style={{ padding: '0 20px' }}>
        <JackpotStrip games={[]} surveys={surveys} />
    </div>
    {showCompletion && (
        <div className={classes.completionBanner} role="status">
            <span aria-hidden="true">✓</span>
            <span>Survey complete! Your reward is on its way — it usually lands in your balance within a few minutes.</span>
            <button
                type="button"
                aria-label="Dismiss"
                className={classes.completionClose}
                onClick={() => setShowCompletion(false)}
            >
                ×
            </button>
        </div>
    )}
    <h2 className={classes.sponsoredOffersHeading}>Available Surveys</h2>
    <ul className={classes.list}>
    {[...surveys]
      .sort((a, b) =>
        (Number(b.conversionRate ?? b.conversion_rate ?? 0) - Number(a.conversionRate ?? a.conversion_rate ?? 0)) ||
        (Number(b.payout || 0) - Number(a.payout || 0))
      )
      .map((survey, index) => (
      <li key={survey.id}>
        <TopOffer
          title_text={survey.title || "Consumer Survey"}
          center_text={"Complete this survey to earn cash!"}
          devices={['*']}
          link={survey.href_new}
          reward_payout={survey.payout}
          events={[{
            eventDescription: "Complete the survey!",
            value: survey.payout
          }]}
          isHot={index === 0}
          hotLabel="⭐ TOP PICK"
          ratingsCount={survey.statistics_rating_count}
          ratingsAvg={survey.statistics_rating_avg}
          glimmer
          directLink
        />
      </li>
    ))}
  </ul>
  </>
  );
};

export default SurveyList;
